// 重构后的聊天组件主文件

import React, { useState, useCallback, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Icon } from '@iconify/react';
import { v4 as uuidv4 } from 'uuid';
import { ChatWidgetProps, FormErrors, ChatWidgetRef } from './types';
import { ChatCache } from './utils/cache';
import { MessageUtils } from './utils/message';
import { createChatApiService } from './services/api';
import { useChatMessages } from './hooks/useChatMessages';
import { useChatPolling, PollingConfig } from './hooks/useChatPolling';
import { useChatScroll } from './hooks/useChatScroll';
import { usePhoneInput } from './hooks/usePhoneInput';
import { MessageItem } from './components/MessageItem';
import VirtualMessageList from './components/VirtualMessageList';
import './index.scss';

const ChatWidget = forwardRef<ChatWidgetRef, ChatWidgetProps>(({
  userName,
  userEmail,
  tempUserKey,
  className = '',
  position = 'bottom-right',
  apiEndpoint,
  sendMessageEndpoint='/collect_message',
  apiKey,
  getMessagesEndpoint = '/get_new_message',
  setReadEndpoint = '/set_message_read',
  pollingInterval = 50000,
  enablePolling = true,
  fromClient = 'manage_site',
  texts = {},
  features = {},
  callbacks = {},
  showCloseButton = false,
  enableMinimize = true,
  brandColor = '#1972f5',
  toggleTextColor = '#1972f5'
}, ref) => {
  // 默认文本配置 - 使用 useMemo 缓存
  const defaultTexts = useMemo(() => ({
    headerTitle: 'Live Chat',
    headerSubtitle: 'We\'re here to help!',
    welcomeMessage: 'Hey there! 👋\nGot a question about DeepBI or need a quick demo?\nDrop us a message with your Phone number — we’ll reply as soon as possible.',
    welcomeBackMessage: 'Hi {userName}! 👋\nWelcome back! How can I help you today?',
    initializationMessage: 'Great! I\'m here to help. What would you like to know?',
    namePlaceholder: 'Name *',
    emailPlaceholder: 'Email *',
    phonePlaceholder: 'Phone Number *',
    messagePlaceholder: 'message...',
    startButtonText: 'Start',
    loadingText: 'Processing...',
    loadingHistoryText: 'Loading history...',
    retryText: 'Retry',
    ...texts
  }), [texts]);

  // 默认功能配置 - 使用 useMemo 缓存
  const defaultFeatures = useMemo(() => ({
    enableHistoryLoading: true,
    enableAutoScroll: true,
    enableSessionCache: true,
    enableMessageReadTracking: true,
    enableMessageRetry: true,
    enableFormValidation: true,
    ...features
  }), [features]);

  // 使用 useMemo 缓存读取数据，避免每次渲染都读取
  const cachedData = useMemo(() => 
    defaultFeatures.enableSessionCache ? ChatCache.load(userEmail) : null, 
    [defaultFeatures.enableSessionCache, userEmail]
  );

  // 基础状态
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  // 初始化 name：优先使用 userName，其次缓存，最后从邮箱提取
  const [name, setName] = useState(() => {
    if (userName) return userName;
    if (cachedData?.name) return cachedData.name;
    if (userEmail) {
      const emailParts = userEmail.split('@');
      return emailParts[0] || '';
    }
    if (cachedData?.email) {
      const emailParts = cachedData.email.split('@');
      return emailParts[0] || '';
    }
    return '';
  });
  const [email, setEmail] = useState(() => userEmail || cachedData?.email || '');
  
  // 手机号输入Hook
  const {
    selectedCountry,
    phoneNumber,
    error: phoneError,
    phoneCountries,
    handleCountryChange,
    handlePhoneChange,
    validatePhone,
    getFullPhoneNumber,
    setFullPhoneNumber,
    setPhoneNumber
  } = usePhoneInput();

  // 初始化手机号（从 props 或 缓存）
  // 只有当 initialValue 不是邮箱格式时才当作手机号处理
  useEffect(() => {
    const initialValue = userEmail || cachedData?.email;
    if (initialValue && !initialValue.includes('@')) {
      setFullPhoneNumber(initialValue);
    }
  }, [userEmail, cachedData?.email]);

  // 同步手机号到 email 状态（仅在手机号模式下）
  useEffect(() => {
    // 如果外部传入了邮箱（包含 @），说明当前是 SaaS 场景，用真实邮箱做标识，不要用手机号覆盖
    const initialValue = userEmail || cachedData?.email;
    if (initialValue && initialValue.includes('@')) {
      return;
    }

    const fullPhone = getFullPhoneNumber();
    if (fullPhone) {
      setEmail(fullPhone);
    }
  }, [phoneNumber, selectedCountry, userEmail,cachedData?.email, getFullPhoneNumber]);

  const [isInitialized, setIsInitialized] = useState(() => !!cachedData);
  const [showNameEmailForm, setShowNameEmailForm] = useState(() => !userName && !userEmail && !cachedData);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [hasInitialMessage, setHasInitialMessage] = useState(false);
  const [currentTempUserKey, setCurrentTempUserKey] = useState(() => tempUserKey || cachedData?.temp_user_key || '');
  const [isMinimized, setIsMinimized] = useState(enableMinimize);
  const [isFirstLoading, setIsFirstLoading] = useState(() => !!cachedData); // 首次加载历史消息状态
  const [loadError, setLoadError] = useState<string | null>(null); // 加载错误信息

  // 创建API服务实例 - 优化缓存
  const apiService = useMemo(() => {
    return createChatApiService({
      endpoint: apiEndpoint,
      sendMessageEndpoint: `${apiEndpoint}${sendMessageEndpoint}`,
      apiKey,
      getMessagesEndpoint: `${apiEndpoint}${getMessagesEndpoint}`,
      setReadEndpoint: `${apiEndpoint}${setReadEndpoint}`
    });
  }, [apiEndpoint, sendMessageEndpoint, apiKey, getMessagesEndpoint, setReadEndpoint]);


  // 从邮箱提取 name 的工具函数
  const extractNameFromEmail = useCallback((email: string): string => {
    if (!email || !email.includes('@')) return '';
    return email.split('@')[0];
  }, []);

  // 使用消息管理 Hook
  const {
    messages,
    setMessages,
    addMessage,
    addMessages, // 新增批量添加方法
    updateMessageStatus,
    markMessageAsRead,
    fetchMessages,
    loadMoreHistory,
    isLoadingHistory,
    hasMoreHistory
  } = useChatMessages(apiService);

  // 计算未读消息数量 - 只在聊天窗口打开时计算
  const unreadCount = useMemo(() => {
    // 如果聊天窗口没有打开，不进行计算
    if (!isOpen) {
      return 0;
    }
    const unreadMessages = messages.filter(message => 
      message.type === 'bot' && 
      (message.readFlag === 0 || message.isNew === true)
    );
    
    return unreadMessages.length;
  }, [messages, isOpen]);

  // 使用滚动管理 Hook - 适配虚拟滚动
  const {
    isUserAtBottom,
    messagesEndRef,
    messagesContainerRef,
    forceScrollToBottom
  } = useChatScroll(
    messages,
    hasMoreHistory,
    isLoadingHistory,
    () => loadMoreHistory(email, currentTempUserKey),
    false // 暂时禁用自动滚动，避免与 VirtualMessageList 冲突
  );

  // 虚拟滚动容器的引用
  const virtualScrollRef = useRef<any>(null);

  // 错误处理回调
  const handleFetchError = useCallback((error: Error) => {
    // 只在首次加载且没有消息时显示错误
    if (isFirstLoading && messages.length === 0) {
      // 根据是否有缓存数据判断用户类型
      const isReturningUser = !!cachedData;
      const errorMessage = isReturningUser 
        ? 'Failed to load conversation history. Please try again.'
        : 'Failed to initialize chat. Please try again.';
      setLoadError(errorMessage);
      setIsFirstLoading(false);
    }
  }, [isFirstLoading, messages.length, loadError, cachedData]);

  // 使用轮询管理 Hook
  const { stopPolling } = useChatPolling(
    isOpen,
    isInitialized,
    email,
    currentTempUserKey,
    fetchMessages,
    {
      interval: pollingInterval,
      enabled: enablePolling,
      delay: 5000,
      onError: handleFetchError
    } as PollingConfig
  );

  const inputRef = useRef<HTMLTextAreaElement>(null);

  // 聚焦输入框
  useEffect(() => {
    if (isOpen && !showNameEmailForm) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen, showNameEmailForm]);

  // 表单验证 - 验证手机号
  const validateForm = useCallback(() => {
    if (!defaultFeatures.enableFormValidation) return true;
    
    // 验证手机号
    const isValid = validatePhone(phoneNumber);
    if (!isValid) {
      // 验证失败，设置错误信息
      // 由于 validatePhone 内部会设置 phoneError，但状态更新是异步的
      // 这里我们需要立即显示错误，所以手动构建错误信息
      const digits = phoneNumber.replace(/\D/g, '');
      let errorMsg = 'Invalid phone number';
      
      if (!phoneNumber || !phoneNumber.trim()) {
        errorMsg = 'Phone number is required';
      } else if (!/^\d+$/.test(digits)) {
        errorMsg = 'Phone number must only contain digits';
      } else {
        const country = phoneCountries.find(c => c.code === selectedCountry);
        if (country && digits.length !== country.length) {
          errorMsg = `Phone number must be ${country.length} digits`;
        }
      }
      
      setFormErrors({ email: errorMsg });
      return false;
    }
    
    setFormErrors({});
    return true;
  }, [phoneNumber, phoneCountries, selectedCountry, defaultFeatures.enableFormValidation, validatePhone]);

  // 当邮箱变化时，如果没有 userName，自动从邮箱提取 name
  // 手机号模式下，不需要从邮箱提取 name
  useEffect(() => {
    if (!userName && email && !cachedData?.name && email.includes('@')) {
      const extractedName = extractNameFromEmail(email);
      if (extractedName) {
        setName(extractedName);
      }
    }
  }, [email, userName, cachedData?.name, extractNameFromEmail]);

  // 发送消息 - 优化依赖项
  const sendMessage = useCallback(async (messageContent: string, isInitialization = false) => {
    // 如果是初始化阶段，不需要检查消息内容
    if (!isInitialization && !messageContent.trim()) return;

    // 如果是初始化阶段，先收集用户信息
    if (!isInitialized) {
      if (!validateForm()) {
        return;
      }

      console.log('name', name);
      console.log('email', email);
      console.log('phoneNumber', phoneNumber);
      console.log('selectedCountry', selectedCountry);

      // 确保 name 有值：如果没有，从邮箱提取
      const finalName = name || extractNameFromEmail(email) || email;

      setIsLoading(true);
      try {
        const result = await apiService.sendMessage({
          email,
          ...(currentTempUserKey && { temp_user_key: currentTempUserKey }),
          temp_name: finalName,
          message: messageContent || email,
          from_client: fromClient
        });

        // 存储返回的 temp_user_key
        if (result.data?.temp_user_key) {
          setCurrentTempUserKey(result.data.temp_user_key);
          // 保存到缓存
          if (defaultFeatures.enableSessionCache) {
            ChatCache.save({
              name: finalName,
              email,
              temp_user_key: result.data.temp_user_key,
              message_key: result.data.message_key || ''
            }, email);
          }
          
          // 更新 name 状态
          if (!name) {
            setName(finalName);
          }
        }

        setIsInitialized(true);
        setShowNameEmailForm(false);
        setIsFirstLoading(true); // 设置加载状态，等待 get_new_message
        setMessages([]); // 清空欢迎消息，显示全屏 loading
        
        // 触发会话开始回调
        callbacks.onSessionStart?.({ name, email });
        
        // 注意：不在这里添加消息，等待 get_new_message 返回后统一显示
      } catch (error) {
        callbacks.onError?.(error as Error, 'initialize');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // 正常发送消息
    const messageId = uuidv4();
    const newMessage = MessageUtils.createMessage(messageContent, 'user', 'sending');
    newMessage.id = messageId;
    newMessage.isNew = false; // 用户发送的消息不需要动画
    // 添加临时标识，防止轮询时重复添加
    newMessage.tempId = `temp_${messageId}`;
    
    setMessages(prev => {
      const allMessages = [...prev, newMessage];
      return MessageUtils.sortMessagesByTimestamp(allMessages);
    });
    setInputValue('');
    
    // 发送消息后滚动到底部
    setTimeout(() => {
      if (virtualScrollRef.current?.scrollToBottom) {
        virtualScrollRef.current.scrollToBottom();
      } else {
        forceScrollToBottom();
      }
    }, 100);

    // 确保 name 有值：如果没有，从邮箱提取
    const finalName = name || extractNameFromEmail(email) || email;

    try {
      const result = await apiService.sendMessage({
        email,
        ...(currentTempUserKey && { temp_user_key: currentTempUserKey }),
        temp_name: finalName,
        message: messageContent,
        from_client: fromClient
      });

      // 存储返回的 temp_user_key
      if (result.data?.temp_user_key) {
        setCurrentTempUserKey(result.data.temp_user_key);
        // 保存到缓存
        if (defaultFeatures.enableSessionCache) {
          ChatCache.save({
            name: finalName,
            email,
            temp_user_key: result.data.temp_user_key,
            message_key: result.data.message_key || ''
          }, email);
        }
      }

      // 更新 name 状态（如果之前为空）
      if (!name) {
        setName(finalName);
      }

      // 更新消息状态为已发送，并添加服务器返回的标识（清除 isNew 防止二次动画）
      setMessages(prev => {
        const updatedMessages = prev.map(msg => {
          if (msg.id === messageId) {
            return { 
              ...msg, 
              status: 'sent' as const,
              isNew: false,
              // 如果服务器返回了message_key，使用它来防止重复
              ...(result.data?.message_key && { messageKey: result.data.message_key }),
              // 如果服务器返回了ID，使用它来防止重复
              ...(result.data?.id && { serverId: result.data.id })
            };
          }
          return msg;
        });
        return MessageUtils.sortMessagesByTimestamp(updatedMessages);
      });

      // 触发消息发送回调 - 使用 ref 避免依赖 messages
      const sentMessage = { 
        id: messageId, 
        content: messageContent, 
        type: 'user' as const, 
        status: 'sent' as const,
        timestamp: new Date()
      };
      callbacks.onMessageSent?.(sentMessage);

      // 如果有机器人回复
      if (result.data?.reply) {
        // 机器人即时回复：标记为新消息，动画会在 useChatMessages 的 useEffect 中自动清除
        const botMsg = MessageUtils.createMessage(result.data.reply, 'bot', 'sent');
        botMsg.isNew = true;
        setMessages(prev => {
          const all = [...prev, botMsg];
          return MessageUtils.sortMessagesByTimestamp(all);
        });
        
        // 机器人回复后滚动到底部
        setTimeout(() => {
          if (virtualScrollRef.current?.scrollToBottom) {
            virtualScrollRef.current.scrollToBottom();
          } else {
            forceScrollToBottom();
          }
        }, 100);
      }
    } catch (error) {
      // 发送失败时也清除 isNew，避免重复动画
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.id === messageId ? { ...msg, status: 'failed' as const, isNew: false } : msg
        );
        return MessageUtils.sortMessagesByTimestamp(updatedMessages);
      });
      callbacks.onError?.(error as Error, 'send');
    }
  }, [
    // 减少依赖项，只保留真正需要的
    name, 
    email, 
    currentTempUserKey, 
    isInitialized, 
    addMessage,
    apiService, 
    validateForm,
    defaultFeatures.enableSessionCache,
    defaultTexts.initializationMessage,
    callbacks,
    forceScrollToBottom,
    fromClient
  ]);

  // 重试发送消息 - 优化依赖项
  const retryMessage = useCallback(async (messageId: string) => {
    // 使用 setMessages 回调获取最新消息，避免依赖 messages
    setMessages(currentMessages => {
      const message = currentMessages.find(msg => msg.id === messageId);
      if (!message || message.type !== 'user') return currentMessages;

      // 更新消息状态为发送中
      const updatedMessages = MessageUtils.updateMessageStatus(
        currentMessages, 
        messageId, 
        'sending', 
        (message.retryCount || 0) + 1
      );

      // 异步处理重试逻辑
      (async () => {
        try {
          // 确保 name 有值：如果没有，从邮箱提取
          const finalName = name || extractNameFromEmail(email) || email;
          
          const result = await apiService.sendMessage({
            email,
            from_client: fromClient,
            message: message.content,
            temp_name: finalName,
            ...(currentTempUserKey && { temp_user_key: currentTempUserKey })
          });

          // 存储返回的 temp_user_key
          if (result.data?.temp_user_key) {
            setCurrentTempUserKey(result.data.temp_user_key);
            // 保存到缓存
            if (defaultFeatures.enableSessionCache) {
              ChatCache.save({
                name: finalName,
                email,
                temp_user_key: result.data.temp_user_key,
                message_key: result.data.message_key || ''
              }, email);
            }
          }
          
          // 更新 name 状态（如果之前为空）
          if (!name) {
            setName(finalName);
          }

          // 更新消息状态为已发送
          setMessages(prev => MessageUtils.updateMessageStatus(prev, messageId, 'sent'));

          // 如果有机器人回复
          if (result.data?.reply) {
            addMessage(result.data.reply, 'bot');
          }
        } catch (error) {
          setMessages(prev => MessageUtils.updateMessageStatus(prev, messageId, 'failed'));
          callbacks.onError?.(error as Error, 'retry');
        }
      })();

      return updatedMessages;
    });
  }, [
    name, 
    email, 
    currentTempUserKey, 
    addMessage,
    apiService,
    defaultFeatures.enableSessionCache,
    callbacks,
    fromClient
  ]);

  // 设置消息已读
  const handleMarkAsRead = useCallback(async (messageKey: string) => {
    if (!defaultFeatures.enableMessageReadTracking) return;
    
    try {
      await apiService.setMessageRead({
        email,
        temp_user_key: currentTempUserKey,
        message_key: messageKey
      });
      markMessageAsRead(messageKey);
      callbacks.onMessageRead?.(messageKey);
    } catch (error) {
    }
  }, [
    email, 
    currentTempUserKey, 
    markMessageAsRead,
    apiService,
    defaultFeatures.enableMessageReadTracking,
    callbacks,
    tempUserKey
  ]);


  // 初始化聊天 - 处理不同场景的初始化
  useEffect(() => {
    // 只有在聊天窗口打开时才初始化
    if (!isOpen) return;
    
    if (!hasInitialMessage && messages.length === 0 && !isInitialized) {
      console.log('初始化聊天');
      // 只在没有预设用户信息时显示欢迎消息
      if (!userName && !userEmail) {
        // 默认欢迎消息
        const msg = MessageUtils.createMessage(defaultTexts.welcomeMessage, 'bot', 'sent');
        msg.isNew = false;
        setMessages([msg]);
        setHasInitialMessage(true);
      } else if (userName && userEmail) {
        console.log('有预设用户信息时，需要先调用 collect_message 获取 temp_user_key');
        console.log('userName:', userName);
        console.log('userEmail:', userEmail);
        console.log('tempUserKey:', tempUserKey);
        // 有预设用户信息时，需要先调用 collect_message 获取 temp_user_key
        setHasInitialMessage(true);
        // 自动触发初始化
        (async () => {
          setIsLoading(true);
          try {
            const result = await apiService.sendMessage({
              email: userEmail,
              ...(tempUserKey && { temp_user_key: tempUserKey }),
              temp_name: userName,
              message: userEmail,
              from_client: fromClient
            });

            // 存储返回的 temp_user_key
            if (result.data?.temp_user_key) {
              setCurrentTempUserKey(result.data.temp_user_key);
              // 保存到缓存
              if (defaultFeatures.enableSessionCache) {
                ChatCache.save({
                  name: userName,
                  email: userEmail,
                  temp_user_key: result.data.temp_user_key,
                  message_key: result.data.message_key || ''
                }, userEmail);
              }
            }

            setIsInitialized(true);
            setShowNameEmailForm(false);
            setIsFirstLoading(true); // 设置加载状态，等待 get_new_message
            
            // 触发会话开始回调
            callbacks.onSessionStart?.({ name: userName, email: userEmail });
          } catch (error) {
            callbacks.onError?.(error as Error, 'initialize');
            // 初始化失败，显示错误
            setLoadError('Failed to initialize chat. Please try again.');
            setIsFirstLoading(false);
          } finally {
            setIsLoading(false);
          }
        })();
      }
    }
  }, [isOpen, userName, userEmail, tempUserKey, hasInitialMessage, isInitialized, messages.length, defaultTexts, setMessages, apiService, fromClient, defaultFeatures.enableSessionCache, callbacks]);

  // 监听首次加载完成
  useEffect(() => {
    if (isFirstLoading && messages.length > 0) {
      setIsFirstLoading(false);
      setLoadError(null); // 清除错误
    }
  }, [isFirstLoading, messages.length]);

  // 重试加载
  const retryLoad = useCallback(() => {
    setLoadError(null);
    setIsFirstLoading(true);
    // 手动触发一次消息获取
    if (email && currentTempUserKey) {
      fetchMessages(email, currentTempUserKey, 1, 10, false).catch(error => {
        // 根据是否有缓存数据判断用户类型
        const isReturningUser = !!cachedData;
        const errorMessage = isReturningUser 
          ? 'Failed to load conversation history. Please try again.'
          : 'Failed to initialize chat. Please try again.';
        setLoadError(errorMessage);
        setIsFirstLoading(false);
      });
    }
  }, [email, currentTempUserKey, fetchMessages, cachedData]);


  // 处理输入框回车
  const handleInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    // 发送中禁止发送；支持 Shift+Enter 换行
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      if (isLoading) return;
      if (showNameEmailForm) {
        sendMessage('', true);
      } else {
        sendMessage(inputValue);
      }
    }
  }, [inputValue, sendMessage, showNameEmailForm]);

  // 处理开始聊天
  const handleStartChat = useCallback(() => {
    if (validateForm()) {
      sendMessage('', true);
    }
  }, [validateForm, sendMessage]);

  // 处理发送消息
  const handleSendMessage = useCallback(() => {
    sendMessage(inputValue);
  }, [sendMessage, inputValue]);

  // 处理输入框变化
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  }, []);

  // 切换聊天框
  const toggleChat = useCallback(() => {
    setIsOpen(!isOpen);
    if (isMinimized) {
      setIsMinimized(false);
    }
  }, [isOpen, isMinimized]);

  // 切换收起/展开状态
  const toggleMinimize = useCallback(() => {
    setIsMinimized(!isMinimized);
  }, [isMinimized]);

  // 退出会话
  const exitSession = useCallback(() => {
    // 清除轮询
    stopPolling();

    // 注意：resetSession 不再清除 localStorage 缓存
    // 如果需要清除特定用户的缓存，可以调用 ChatCache.clear(email)

    // 重置所有状态
    setMessages([]);
    setInputValue('');
    setIsInitialized(false);
    setShowNameEmailForm(!userName || !userEmail);
    setFormErrors({});
    setHasInitialMessage(false);
    setCurrentTempUserKey('');
    setIsLoading(false);

    // 如果没有预设的用户信息，也重置用户输入
    if (!userName) setName('');
    if (!userEmail) setEmail('');

    // 触发会话结束回调
    callbacks.onSessionEnd?.();
  }, [userName, userEmail, stopPolling, defaultFeatures.enableSessionCache, callbacks]);

  // 暴露给外部的方法
  useImperativeHandle(ref, () => {
    const internalSendMessage = sendMessage; // 保存内部函数引用
    
    return {
      // 重置会话
      resetSession: exitSession,
      
      // 打开聊天窗口
      openChat: () => setIsOpen(true),
      
      // 关闭聊天窗口
      closeChat: () => setIsOpen(false),
      
      // 发送消息（编程方式）
      sendMessage: (message: string) => {
        if (message.trim() && isInitialized) {
          internalSendMessage(message.trim());
        }
      },
      
      // 获取当前会话状态
      getSessionInfo: () => ({
        isOpen,
        isInitialized,
        messageCount: messages.length,
        unreadCount,
        userInfo: {
          name,
          email,
          tempUserKey: currentTempUserKey
        }
      })
    };
  }, [exitSession, isOpen, isInitialized, sendMessage, messages.length, unreadCount, name, email, currentTempUserKey]);

  return (
    <div 
      className={`chat-widget ${className} chat-widget--${position} ${isMinimized ? 'chat-widget--minimized' : ''}`}
      style={{
        '--chat-brand-color': brandColor,
        '--chat-toggle-text-color': toggleTextColor
      } as React.CSSProperties}
    >
      {/* 聊天图标/关闭按钮 */}
      <div 
        className={`chat-widget__toggle ${isOpen ? 'chat-widget__toggle--open' : ''} ${isMinimized ? 'chat-widget__toggle--minimized' : ''}`}
        onClick={toggleChat}
        onMouseEnter={() => enableMinimize && !isOpen && setIsMinimized(false)}
        onMouseLeave={() => enableMinimize && !isOpen && setIsMinimized(true)}
      >
        {isOpen ? (
          <Icon icon="ph:x" className="chat-widget__toggle-icon" />
        ) : (
          <Icon icon="radix-icons:chat-bubble" className="chat-widget__toggle-icon" />
        )}
        
        {/* 未读消息指示器 */}
        {!isOpen && unreadCount > 0 && (
          <div className="chat-widget__unread-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </div>
        )}
        
        {/* Contact us 文字 */}
        {!isOpen && (
          <div className="chat-widget__toggle-text">
            Contact us
          </div>
        )}
      </div>

      {/* 聊天框 */}
      <div className={`chat-widget__container ${isOpen ? 'chat-widget__container--open' : ''}`}>

        {/* 首次加载历史消息的全屏 loading - 覆盖整个聊天窗口 */}
        {isFirstLoading && messages.length === 0 && !loadError && (
          <div className="chat-widget__fullscreen-loading">
            <div className="chat-widget__loading-spinner">
              <div className="chat-widget__spinner"></div>
            </div>
            <span>{defaultTexts.loadingText || 'Loading conversation history...'}</span>
          </div>
        )}
        
        {/* 加载失败错误提示 */}
        {loadError && messages.length === 0 && (
          <div className="chat-widget__fullscreen-error">
            <Icon icon="ph:warning-circle" className="chat-widget__error-icon" />
            <span className="chat-widget__error-message">{loadError}</span>
            <button className="chat-widget__retry-btn" onClick={retryLoad}>
              <Icon icon="ph:arrow-clockwise" />
              Retry
            </button>
          </div>
        )}
        
        {/* 头部 */}
        <div className="chat-widget__header">
          <div className="chat-widget__header-avatar">
            <div className="chat-widget__avatar">
              <img src="https://www.deepbi.com/images/Logo/Deepinsight.png" alt="" />
            </div>
          </div>
          <div className="chat-widget__header-info">
            <div className="chat-widget__header-title">
              {defaultTexts.headerTitle}
              {unreadCount > 0 && (
                <span className="chat-widget__header-unread">
                  ({unreadCount} unread)
                </span>
              )}
            </div>
            <div className="chat-widget__header-subtitle">{defaultTexts.headerSubtitle}</div>
          </div>
          {showCloseButton && !showNameEmailForm && (
            <button 
              className="chat-widget__header-close"
              onClick={exitSession}
              title="退出会话"
            >
              <Icon icon="ph:sign-out" />
            </button>
          )}
        </div>

        {/* 消息区域 - 使用虚拟滚动优化 */}
        <div className="chat-widget__messages-container">
          {/* 未读消息提示条 */}
          {unreadCount > 0 && (
            <div className="chat-widget__unread-notice">
              <span>{unreadCount} unread message{unreadCount > 1 ? 's' : ''}</span>
              <button 
                className="chat-widget__scroll-to-unread-btn"
                onClick={() => {
                  if (virtualScrollRef.current?.scrollToBottom) {
                    virtualScrollRef.current.scrollToBottom();
                  } else {
                    forceScrollToBottom();
                  }
                }}
              >
                View latest
              </button>
            </div>
          )}
          
          {/* 虚拟滚动消息列表 */}
          <VirtualMessageList
            ref={virtualScrollRef}
            messages={messages}
            onRetry={defaultFeatures.enableMessageRetry ? retryMessage : undefined}
            onMarkAsRead={defaultFeatures.enableMessageReadTracking ? handleMarkAsRead : undefined}
            itemHeight={80}
            overscan={5}
            hasMoreHistory={hasMoreHistory}
            isLoadingHistory={isLoadingHistory}
            onLoadMoreHistory={() => loadMoreHistory(email, currentTempUserKey)}
            isVisible={isOpen}
          />
          
          {/* 滚动到底部锚点 */}
          <div ref={messagesEndRef} />
        </div>

        {/* 输入区域 */}
        <div className="chat-widget__input-area">
          {showNameEmailForm ? (
            <div className="chat-widget__form">
              <div className="chat-widget__form-row">
                <div className={`chat-widget__phone-container ${formErrors.email ? 'chat-widget__input--error' : ''}`}>
                  <div className="chat-widget__country-selector">
                    <Icon 
                      icon={phoneCountries.find(c => c.code === selectedCountry)?.icon || 'twemoji:flag-united-states'} 
                      width="20" 
                      height="20"
                    />
                    <select
                      className="chat-widget__country-select"
                      value={selectedCountry}
                      onChange={(e) => handleCountryChange(e.target.value)}
                      disabled={isLoading}
                    >
                      {phoneCountries.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.code} {country.dialCode}
                        </option>
                      ))}
                    </select>
                    <Icon 
                      icon="mdi:chevron-down" 
                      width="16" 
                      height="16"
                      className="chat-widget__country-arrow"
                    />
                  </div>
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="chat-widget__phone-input"
                    placeholder={defaultTexts.phonePlaceholder}
                    value={phoneNumber}
                    onChange={(e) => {
                      handlePhoneChange(e.target.value);
                      if (formErrors.email) {
                        setFormErrors({});
                      }
                    }}
                    onKeyDown={handleInputKeyDown}
                    disabled={isLoading}
                  />
                </div>
                {formErrors.email && (
                  <div className="chat-widget__form-error">{formErrors.email}</div>
                )}
              </div>
              <button
                type="button"
                className="chat-widget__start-btn"
                onClick={handleStartChat}
              >
                {isLoading ? (
                  <>
                    <div className="chat-widget__btn-spinner"></div>
                    {defaultTexts.loadingText}
                  </>
                ) : (
                  defaultTexts.startButtonText
                )}
              </button>
            </div>
          ) : (
            <div className="chat-widget__input-wrapper">
              <textarea
                ref={inputRef}
                className="chat-widget__chat-input"
                placeholder={defaultTexts.messagePlaceholder}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                disabled={isLoading}
                rows={1}
              />
              <button
                type="button"
                className="chat-widget__send-btn"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
              >
                <Icon className='Icon' icon="mynaui:send" />
              </button>
            </div>
          )}
        </div>

        {/* 加载状态 */}
        {/* {isLoading && (
          <div className="chat-widget__loading">
            <div className="chat-widget__loading-spinner">
              <div className="chat-widget__spinner"></div>
            </div>
            <span>{defaultTexts.loadingText}</span>
          </div>
        )} */}
      </div>
    </div>
  );
});

// 设置组件显示名称，便于调试
ChatWidget.displayName = 'ChatWidget';

export default ChatWidget;
